// Pending Invoices tab — cross-user UNPAID list. Each row has a Pay button
// that opens the same PayInvoiceDialog used on the user detail page.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, Paper, Pagination, Chip, CircularProgress,
    Alert, Button, Snackbar, Stack, IconButton, Tooltip,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';

import axiosInstance from '../utils/axiosInstance';
import { formatCurrency, formatDate, titleCase } from '../utils/format';
import { useDebounce } from '../utils/useDebounce';
import PayInvoiceDialog from '../components/PayInvoiceDialog';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 25;

const PendingInvoices = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState(null);
    const [payInvoice, setPayInvoice] = useState(null);

    const debouncedSearch = useDebounce(search, 350);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            const res = await axiosInstance.get('/admin/invoices/pending', { params });
            setInvoices(res.data?.invoices || []);
            setPages(res.data?.pages || 1);
            setTotal(res.data?.total || 0);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load invoices.');
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch]);

    useEffect(() => { load(); }, [load]);

    const onPaid = () => {
        setSnackbar({ severity: 'success', message: 'Invoice settled. Subscription reset.' });
        setPayInvoice(null);
        load();
    };

    return (
        <Box sx={{ fontFamily }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                   alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                        Pending Invoices
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        Every UNPAID subscription invoice across the customer base.
                    </Typography>
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                    {total} pending
                </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                    size="small" label="Search invoice # or email" value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    sx={{ minWidth: 320 }}
                />
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            ) : invoices.length === 0 ? (
                <Typography sx={{ fontFamily, color: '#64748b', py: 6, textAlign: 'center' }}>
                    No pending invoices.
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                        <Table aria-label="pending invoices">
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Invoice</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Customer</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Plan</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Issued</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Due</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Amount</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoices.map((inv) => (
                                    <TableRow key={inv.id} hover>
                                        <TableCell sx={{ fontFamily, fontWeight: 600 }}>
                                            {inv.invoice_number}
                                            <Chip
                                                size="small" label={titleCase(inv.billing_reason)}
                                                sx={{ fontFamily, ml: 1, height: 18, fontSize: '0.65rem' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }}>
                                            {inv.billed_to_email || `#${inv.team_owner_id}`}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {inv.plan_name || '--'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }}>{formatDate(inv.issued_at)}</TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {formatDate(inv.due_at)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily }} align="right">
                                            {formatCurrency(inv.total, inv.currency)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Open user">
                                                <IconButton size="small"
                                                            onClick={() => navigate(`/users/${inv.team_owner_id}`)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                size="small" variant="contained"
                                                startIcon={<PaymentIcon fontSize="small" />}
                                                onClick={() => setPayInvoice(inv)}
                                                sx={{ fontFamily, textTransform: 'none', fontWeight: 700, ml: 1 }}
                                            >
                                                Pay
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination count={pages} page={page}
                                        onChange={(_, p) => setPage(p)}
                                        size="small" color="primary" />
                        </Box>
                    )}
                </>
            )}

            <PayInvoiceDialog
                open={!!payInvoice} invoice={payInvoice}
                onClose={() => setPayInvoice(null)}
                onPaid={onPaid}
            />

            <Snackbar
                open={!!snackbar} autoHideDuration={4500} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ fontFamily }}>
                        {snackbar.message}
                    </Alert>
                ) : null}
            </Snackbar>
        </Box>
    );
};

export default PendingInvoices;
